import { EnvDetector } from "../EnvDetector";
import { MessageBus } from "../MessageBus/MessageBus";
import { DeleteCommand } from "./Messages/DeleteCommand";
import { DropCommand } from "./Messages/DropCommand";
import { FindQuery } from "./Messages/FindQuery";
import { ListQuery } from "./Messages/ListQuery";
import { MoveCommand } from "./Messages/MoveCommand";
import { ReadQuery } from "./Messages/ReadQuery";
import { UpdateCommand } from "./Messages/UpdateCommand";
import { IRepo } from "./IRepo";


export class OnlineRepo<T extends object> implements IRepo<T>
{
	public LOG = false;
	private readonly storageFolder: string;

	constructor(private _bus: MessageBus, private _env: EnvDetector, private folder: string) 
	{ 
		this.storageFolder = `Specteam/${this._env.Env}/${this.folder}`;
	}

	private Log(...message: any[])
	{
		if (this.LOG)
			console.log('[OnlineRepo]', ...message);
	}

	public async Ping(): Promise<boolean>
	{
		return await this._bus.Ping();
	}

	public async GetOne(folder: string, id: string): Promise<T | null>
	{
		this.Log(`Fetching ${id}...`)
		const folderPath = this.storageFolder + "/" + folder;
		const queryResult = await this._bus.SendMessage(new ReadQuery(id, folderPath));
		if (queryResult.IsSuccess)
		{
			const item = queryResult.Result;
			this.Log(`Found.`)
			return item;
		}
		this.Log(`Not found.`)
		return null;
	}

	public async FindOne(id: string): Promise<T | null>
	{
		this.Log(`Fetching ${id}...`)
		const queryResult = await this._bus.SendMessage(new FindQuery(id, this.storageFolder));
		if (queryResult.IsSuccess)
		{
			const item = queryResult.Result;
			this.Log(`Found.`)
			return item;
		}
		this.Log(`Not found.`)
		return null;
	}

	public async GetAll(folder: string): Promise<T[]>
	{
		this.Log(`Fetching all from ${this.storageFolder}...`)
		const folderPath = this.storageFolder + "/" + folder;
		const queryResult = await this._bus.SendMessage(new ListQuery(folderPath, true));

		if (queryResult.IsSuccess)
		{
			const items = queryResult.Result;
			console.log('Fetched', items.length, 'items from', folderPath)
			return items;
		}
		else throw new Error(`Could not fetch all from ${folder}: ${queryResult.ErrorMessage}`);
	}

	public async GetAllIds(folder: string): Promise<string[]>
	{
		this.Log(`Fetching all from ${this.storageFolder}...`)
		const folderPath = this.storageFolder + "/" + folder;
		const queryResult = await this._bus.SendMessage(new ListQuery(folderPath, false));

		if (queryResult.IsSuccess)
		{
			const itemsIds = Object.values(queryResult.Result);
			console.log('Fetched', itemsIds.length, 'items ids from', folderPath)
			return itemsIds as string[];
		}
		else throw new Error(`Could not fetch all ids from ${folder}: ${queryResult.ErrorMessage}`);
	}

	public async Update(folder: string, id: string, item: T): Promise<void>
	{
		this.Log(`Updating ${id}...`)
		const folderPath = this.storageFolder + "/" + folder;

		await this._bus.SendMessage(new UpdateCommand(id, folderPath, item));
		this.Log(`Updated in repo.`)
	}

	public async Delete(folder: string, id: string): Promise<void>
	{
		const folderPath = this.storageFolder + "/" + folder;

		await this._bus.SendMessage(new DeleteCommand(id, folderPath));
	}

	public async Drop(): Promise<void>
	{
		this.Log(`Dropping ${this.storageFolder}...`)
		await this._bus.SendMessage(new DropCommand(this.storageFolder));
		this.Log(`Dropped ${this.storageFolder}.`)
	}

	public async Move(sourceFolder: string, targetFolder: string, id: string): Promise<void>
	{
		const source = this.storageFolder + "/" + sourceFolder;
		const target = this.storageFolder + "/" + targetFolder;

		this.Log(`Moving ${id} from ${source} to ${target}...`)

		const result = await this._bus.SendMessage(new MoveCommand(id, source, target));

		if (!result.IsSuccess)
			throw new Error(`Could not move ${id} from ${source} to ${target}: ${result.ErrorMessage}`);

		this.Log(`Moved ${id} from ${source} to ${target}.`)
	}
}
